import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { capture } from "@/lib/analytics";
import { motion } from "framer-motion";
import { Lock, Check, Sparkles, Briefcase, Image as ImageIcon, Brain, Trophy, Flame, Star, Gift, ArrowRight } from "lucide-react";
import { BeltProgress, BeltLadder, beltForXP } from "@/components/BeltSystem";
import SkillJourney from "@/components/SkillJourney";
import ReferralCodeInput from "@/components/ReferralCodeInput";
import PushOptInBanner from "@/components/PushOptInBanner";
import StreakRewardBanner from "@/components/StreakRewardBanner";
import FocusedChallenges from "@/components/FocusedChallenges";
import { AmericaPromoCard } from "@/lib/america250";
import { captureNudgeArrival, identify } from "@/lib/analytics";

const ICONS = { sparkles: Brain, briefcase: Briefcase, image: ImageIcon };

function PathCard({ path, onPick }) {
  const Icon = ICONS[path.icon] || Sparkles;
  const lessons = path.lessons;
  const accentMap = { basics: "var(--primary)", work: "var(--secondary)", image: "var(--streak)" };
  const accent = accentMap[path.key] || "var(--primary)";

  return (
    <div className="brut p-7 brut-hover" data-testid={`path-card-${path.key}`}>
      <div className="flex items-start justify-between mb-5">
        <div>
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
            style={{ background: accent }}
          >
            <Icon size={18} color="#fff" />
          </div>
          <span className="kicker">{path.tagline}</span>
          <h3 className="font-display text-2xl mt-1 leading-tight">{path.name}</h3>
        </div>
        <span className="pill">{path.completed_count}/{path.total_count}</span>
      </div>

      <div className="relative pt-2">
        {lessons.map((l, idx) => {
          const offset = idx % 4;
          const left = ["0%", "20%", "40%", "20%"][offset];
          const isCurrent = !l.completed && l.unlocked && (idx === 0 || lessons[idx - 1]?.completed);
          const status = l.completed ? "completed" : !l.unlocked ? "locked" : isCurrent ? "current" : "current";
          return (
            <div
              key={l.id}
              className="flex items-center gap-4 mb-4"
              style={{ paddingLeft: left, transition: "padding 0.3s" }}
            >
              <button
                onClick={() => l.unlocked && onPick(l)}
                disabled={!l.unlocked}
                data-testid={`learning-node-${l.id}`}
                className={`node ${status}`}
                title={l.title}
              >
                {l.completed ? (
                  <Check size={26} />
                ) : !l.unlocked ? (
                  <Lock size={20} />
                ) : l.is_pro ? (
                  <Star size={22} fill={accent} stroke={accent} />
                ) : (
                  <span className="text-2xl">{l.order}</span>
                )}
              </button>
              <div>
                <div className="font-display text-xl leading-tight">
                  {l.title}{" "}
                  {l.is_pro && (
                    <span className="pill pill-accent ml-1.5" style={{ fontSize: "0.6rem", padding: "0.18rem 0.5rem" }}>
                      Mastery
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--ink-2)] mt-1">
                  {l.concept}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/dashboard");
        setData(r.data);
        // Identify in PostHog + fire nudge-arrival event if the URL has UTM
        if (r.data?.user?.user_id) {
          identify(r.data.user.user_id, {
            email: r.data.user.email,
            tier: r.data.user.tier,
            streak: r.data.user.streak,
          });
        }
        captureNudgeArrival("nudge_arrival_dashboard");
      } catch (e) {
        if (e?.response?.status === 401) navigate("/");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const onPickLesson = (l) => {
    if (l.tier_locked) {
      navigate("/pricing");
      return;
    }
    if (!l.unlocked) return;
    navigate(`/lesson/${l.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="spinner" />
      </div>
    );
  }
  if (!data) return null;

  const u = data.user || {};
  const isSubscriber = !!(u.is_pro || u.is_career || u.has_subscription);
  const showReferralPrompt = !isSubscriber && !u.has_referral_discount;

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12 pb-28 md:pb-12">
        {/* America 250 — only renders during the campaign window */}
        <div className="mb-6"><AmericaPromoCard /></div>

        {/* Streak reward — eligible only after 10-day streak + 90-day cooldown */}
        <StreakRewardBanner />

        {/* Push notification opt-in (logged-in users, not yet subscribed) */}
        <PushOptInBanner />

        {/* Referral code prompt (free users only) */}
        {showReferralPrompt && (
          <div
            className="brut p-4 md:p-5 mb-6 flex items-center justify-between gap-4 flex-wrap"
            style={{
              background:
                "linear-gradient(135deg, rgba(14,159,110,0.06) 0%, rgba(99,102,241,0.05) 100%)",
              borderColor: "rgba(14,159,110,0.25)",
            }}
            data-testid="dashboard-referral-banner"
          >
            <div className="flex items-start gap-3 min-w-0">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "var(--emerald, #0E9F6E)" }}
              >
                <Gift size={16} color="#fff" />
              </div>
              <div className="min-w-0">
                <div className="font-medium text-[var(--ink)]">
                  Got a referral code?
                </div>
                <div className="text-sm text-[var(--ink-2)] mt-0.5">
                  Apply it now — unlock 90% off AI Mastery Monthly or 30% off Annual at checkout.
                </div>
              </div>
            </div>
            <ReferralCodeInput centered={false} className="flex-shrink-0" />
          </div>
        )}

        {/* Greeting + Belt status */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-8">
          <div className="lg:col-span-3 card-elevated p-7 md:p-9 relative overflow-hidden" data-testid="dashboard-greeting" style={{ background: "linear-gradient(135deg, var(--surface) 0%, var(--primary-soft) 100%)" }}>            <div className="absolute -top-12 -right-8 w-40 h-40 rounded-full bg-[var(--primary)] opacity-10 blur-2xl pointer-events-none" />
            <span className="kicker">Welcome back</span>
            <h1 className="font-display text-4xl md:text-5xl mt-3 leading-[1.05] tracking-[-0.02em] font-semibold">
              Hi, <em>{(data.user.name?.split(" ")[0] || "").replace(/\.$/, "")}</em>.
            </h1>
            <p className="mt-3 text-[var(--ink-2)] max-w-md leading-relaxed">
              Pick up where you left off. Every lesson moves you closer to your next belt.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[var(--line)]" data-testid="dashboard-streak-chip">
                <Flame size={14} className="text-[var(--streak)] flame" />
                <span className="font-semibold text-[var(--ink)] text-sm" data-testid="dashboard-streak-value">{data.user.streak}</span>
                <span className="text-xs text-[var(--ink-3)]">day streak</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[var(--line)]" data-testid="dashboard-rank-chip">
                <Trophy size={14} className="text-[var(--secondary)]" />
                <span className="font-semibold text-[var(--ink)] text-sm">Rank #{data.rank}</span>
              </div>
            </div>
            {!isSubscriber && (
              <Link to="/pricing" className="btn-duo mt-6" data-testid="dashboard-upgrade-btn">
                Unlock AI Mastery → $9.99/mo
              </Link>
            )}
          </div>

          <motion.div
            className="lg:col-span-2"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05 }}
          >
            <BeltProgress xp={data.user.xp || 0} />
          </motion.div>
        </div>

        {/* AI Coach — paid feature shortcut for paid users, paywall card for free.
         *  Sits above the skill journey so it lands as the first daily-return
         *  hook on the dashboard. */}
        <div className="mb-8" data-testid="dashboard-ai-coach">
          <Link
            to="/coach"
            onClick={() => capture("ai_coach_clicked", { source: "dashboard_card", is_paid: !!isSubscriber })}
            className="brut p-5 sm:p-6 flex items-start gap-4 hover:border-[var(--ink)] transition-colors group"
            style={{
              background: isSubscriber
                ? "linear-gradient(135deg, rgba(37,99,235,0.06), #FFFFFF 70%)"
                : "linear-gradient(135deg, rgba(37,99,235,0.04), rgba(124,58,237,0.04))",
              borderColor: "rgba(37,99,235,0.3)",
            }}
            data-testid={isSubscriber ? "dashboard-coach-ask" : "dashboard-coach-unlock"}
          >
            <div className="w-12 h-12 rounded-xl bg-[var(--primary,#2563EB)] text-white flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--ink-3)]">
                Your AI Coach
              </div>
              <h3 className="font-display text-lg sm:text-xl tracking-tight leading-tight mt-1">
                {isSubscriber
                  ? "Need help with anything?"
                  : "Unlock your personal AI coach"}
              </h3>
              <p className="text-sm text-[var(--ink-2)] mt-1.5 leading-relaxed">
                Get help choosing what to practice next and how to improve.
              </p>
              <div
                className="mt-3 text-sm font-semibold inline-flex items-center gap-1.5"
                style={{ color: "var(--primary,#2563EB)" }}
              >
                {isSubscriber ? "Ask My Coach" : "Unlock AI Coach"}
                <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
              </div>
            </div>
          </Link>
        </div>

        {/* Skill Journey — Day 1 → current prompt-skill progression */}
        <div className="mb-10" data-testid="dashboard-skill-journey">
          <SkillJourney />
        </div>

        {/* Belt ladder */}
        <div className="mb-10" data-testid="dashboard-belt-ladder">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl md:text-2xl tracking-tight">Mastery path</h2>
            <span className="text-xs text-[var(--ink-3)]">8 belts · White → Black</span>
          </div>
          <BeltLadder currentXP={data.user.xp || 0} />
        </div>

        {/* Personalized recommendations driven by user.focuses */}
        <FocusedChallenges />

        {/* Paths */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {data.paths.map((p) => (
            <PathCard key={p.key} path={p} onPick={onPickLesson} />
          ))}
        </div>
      </main>
    </div>
  );
}
